import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import {
  CursorArrowRaysIcon,
  ArrowsPointingOutIcon,
  MapIcon,
  PlayIcon,
  VideoCameraIcon,
  EyeIcon,
  EyeSlashIcon,
  CodeBracketIcon,
  TrashIcon,
  ListBulletIcon,
  ChartBarSquareIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import SessionReplayModal from '../../components/rum/SessionReplayModal';
import { journeysService, PopularPage, ScrollDepthData, SessionData } from '../../services/api/journeys.service';
import rumService from '../../services/api/rum.service';
import productAnalyticsService from '../../services/api/product-analytics.service';
import { sankey as d3Sankey, sankeyLinkHorizontal, SankeyNode, SankeyLink } from 'd3-sankey';

const SANKEY_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

interface SankeyNodeData {
  name: string;
  index?: number;
}

interface SankeyLinkData {
  source: number;
  target: number;
  value: number;
}

const SankeyDiagram: React.FC<{
  paths: Array<{ sequence: string[]; count: number; conversionRate: number }>;
}> = ({ paths }) => {
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { nodes, links, sankeyData } = useMemo(() => {
    if (!paths || paths.length === 0) return { nodes: [], links: [], sankeyData: null };

    const nodeMap = new Map<string, number>();
    const nodeList: SankeyNodeData[] = [];
    const linkList: SankeyLinkData[] = [];

    // Create nodes with step prefix to make them unique per position
    paths.forEach(p => {
      p.sequence.forEach((step, i) => {
        const key = `${i}:${step}`;
        if (!nodeMap.has(key)) {
          nodeMap.set(key, nodeList.length);
          nodeList.push({ name: step });
        }
      });
    });

    // Create links between consecutive steps
    const linkMap = new Map<string, number>();
    paths.forEach(p => {
      for (let i = 0; i < p.sequence.length - 1; i++) {
        const srcKey = `${i}:${p.sequence[i]}`;
        const tgtKey = `${i+1}:${p.sequence[i+1]}`;
        const src = nodeMap.get(srcKey)!;
        const tgt = nodeMap.get(tgtKey)!;
        const linkKey = `${src}-${tgt}`;
        if (linkMap.has(linkKey)) {
          const idx = linkMap.get(linkKey)!;
          linkList[idx].value += p.count;
        } else {
          linkMap.set(linkKey, linkList.length);
          linkList.push({ source: src, target: tgt, value: p.count });
        }
      }
    });

    if (nodeList.length < 2 || linkList.length === 0) return { nodes: nodeList, links: linkList, sankeyData: null };

    try {
      const sankeyGen = d3Sankey<SankeyNodeData, SankeyLinkData>()
        .nodeWidth(20)
        .nodePadding(15)
        .extent([[10, 10], [780, 400]]);

      const data = sankeyGen({
        nodes: nodeList.map(n => ({ ...n })),
        links: linkList.map(l => ({ ...l })),
      });

      return { nodes: nodeList, links: linkList, sankeyData: data };
    } catch {
      return { nodes: nodeList, links: linkList, sankeyData: null };
    }
  }, [paths]);

  if (!sankeyData || !sankeyData.nodes.length) {
    return <div className="text-center py-12 text-gray-500">No path data available for Sankey visualization</div>;
  }

  const linkPath = sankeyLinkHorizontal();

  return (
    <svg ref={svgRef} width="100%" viewBox="0 0 800 420" className="overflow-visible">
      {/* Links */}
      {sankeyData.links.map((link: any, i: number) => (
        <g key={`link-${i}`}>
          <path
            d={linkPath(link) || ''}
            fill="none"
            stroke={SANKEY_COLORS[((link.source as any).index || 0) % SANKEY_COLORS.length]}
            strokeOpacity={hoveredLink === i ? 0.6 : 0.3}
            strokeWidth={Math.max(link.width || 1, 1)}
            onMouseEnter={() => setHoveredLink(i)}
            onMouseLeave={() => setHoveredLink(null)}
            className="cursor-pointer transition-opacity"
          />
          {hoveredLink === i && (
            <title>{`${(link.source as any).name} → ${(link.target as any).name}: ${link.value} users`}</title>
          )}
        </g>
      ))}

      {/* Nodes */}
      {sankeyData.nodes.map((node: any, i: number) => {
        const x0 = node.x0 || 0;
        const y0 = node.y0 || 0;
        const x1 = node.x1 || 0;
        const y1 = node.y1 || 0;
        return (
          <g key={`node-${i}`}>
            <rect
              x={x0}
              y={y0}
              width={x1 - x0}
              height={Math.max(y1 - y0, 1)}
              fill={SANKEY_COLORS[i % SANKEY_COLORS.length]}
              rx={3}
            />
            <text
              x={x0 < 400 ? x1 + 6 : x0 - 6}
              y={(y0 + y1) / 2}
              dy="0.35em"
              textAnchor={x0 < 400 ? 'start' : 'end'}
              fontSize={11}
              fill="currentColor"
              className="text-gray-700 dark:text-gray-300"
            >
              {node.name}
            </text>
            <title>{`${node.name}: ${node.value || 0} users`}</title>
          </g>
        );
      })}
    </svg>
  );
};

type HeatmapType = 'clicks' | 'scroll' | 'movement';

// Professional heatmap canvas component with blur effect
const HeatmapCanvas: React.FC<{
  clicks: Array<{ pageX: number; pageY: number; viewportWidth?: number; viewportHeight?: number }>;
  width: number;
  height: number;
}> = ({ clicks, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || clicks.length === 0) return;

    // Validate dimensions
    if (!width || !height || width <= 0 || height <= 0) {
      console.warn('[HeatmapCanvas] Invalid dimensions:', { width, height });
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Create temporary canvas for blur effect
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    // Create heat points with radial gradients on temp canvas
    clicks.forEach((click) => {
      const x = click.pageX * 0.5; // Scale down by 50% (same as screenshot)
      const y = click.pageY * 0.5;

      // MUITO MAIOR - raio de 150px para ficar bem visível
      const radius = 150;

      // Create radial gradient (center = hot, edges = transparent)
      const gradient = tempCtx.createRadialGradient(x, y, 0, x, y, radius);

      // Professional heatmap colors: center red -> orange -> yellow -> transparent
      gradient.addColorStop(0, 'rgba(255, 0, 0, 0.9)');      // Red center mais intenso
      gradient.addColorStop(0.15, 'rgba(255, 50, 0, 0.7)');  // Laranja forte
      gradient.addColorStop(0.3, 'rgba(255, 100, 0, 0.5)');  // Laranja
      gradient.addColorStop(0.5, 'rgba(255, 200, 0, 0.3)');  // Amarelo
      gradient.addColorStop(0.7, 'rgba(255, 255, 0, 0.15)'); // Amarelo claro
      gradient.addColorStop(1, 'rgba(255, 255, 0, 0)');      // Transparent

      // Draw circle with gradient
      tempCtx.fillStyle = gradient;
      tempCtx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    });

    // Apply blur filter and draw to main canvas - blur MAIOR para efeito profissional
    ctx.filter = 'blur(40px)';
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.filter = 'none';

  }, [clicks, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute top-0 left-0 pointer-events-none"
      style={{
        zIndex: 10,
        mixBlendMode: 'multiply',
        opacity: 0.8,
        width: '100%',
        height: '100%',
      }}
    />
  );
};

// Mouse movement heatmap canvas - density visualization
const MouseMovementCanvas: React.FC<{
  movements: Array<{ x: number; y: number }>;
  width: number;
  height: number;
}> = ({ movements, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || movements.length === 0) return;

    if (!width || !height || width <= 0 || height <= 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);

    // Create density map
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;

    // Draw movement trails with gradient
    movements.forEach((move, index) => {
      const x = move.x * 0.5;
      const y = move.y * 0.5;

      // Menor radius para movimento (mais suave)
      const radius = 60;

      const gradient = tempCtx.createRadialGradient(x, y, 0, x, y, radius);

      // Cores mais suaves para movimento (azul/roxo)
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.4)');   // Blue center
      gradient.addColorStop(0.3, 'rgba(147, 51, 234, 0.3)'); // Purple
      gradient.addColorStop(0.6, 'rgba(236, 72, 153, 0.2)'); // Pink
      gradient.addColorStop(1, 'rgba(236, 72, 153, 0)');     // Transparent

      tempCtx.fillStyle = gradient;
      tempCtx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    });

    // Apply blur
    ctx.filter = 'blur(30px)';
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.filter = 'none';

  }, [movements, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute top-0 left-0 pointer-events-none"
      style={{
        zIndex: 10,
        mixBlendMode: 'multiply',
        opacity: 0.7,
        width: '100%',
        height: '100%',
      }}
    />
  );
};

export default function UserJourneysPage() {
  const { t } = useTranslation('analytics');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [selectedPage, setSelectedPage] = useState('/');
  const [heatmapType, setHeatmapType] = useState<HeatmapType>('clicks');
  const [deviceType, setDeviceType] = useState('all');
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '7d',
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const sessionsPerPage = 10;

  // Real data states
  const [popularPages, setPopularPages] = useState<PopularPage[]>([]);
  const [pageSearch, setPageSearch] = useState('');
  const [pageDropdownOpen, setPageDropdownOpen] = useState(false);
  const pageSelectorRef = useRef<HTMLDivElement>(null);
  const [heatmapData, setHeatmapData] = useState<any>(null);
  const [scrollDepthData, setScrollDepthData] = useState<ScrollDepthData | null>(null);
  const [recentSessions, setRecentSessions] = useState<SessionData[]>([]);
  const [sessionsPagination, setSessionsPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [pageSnapshot, setPageSnapshot] = useState<any>(null);
  const [pageScreenshot, setPageScreenshot] = useState<{ screenshot: string; width: number; height: number } | null>(null);
  const snapshotContainerRef = useRef<HTMLDivElement>(null);

  // Modal state
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Viewed filter
  const [viewedFilter, setViewedFilter] = useState<'all' | 'viewed' | 'unviewed'>('all');

  // Batch selection
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);

  // Path analysis / Sankey state
  const [pathViewMode, setPathViewMode] = useState<'list' | 'sankey'>('list');
  const [commonPaths, setCommonPaths] = useState<Array<{ sequence: string[]; count: number; conversionRate: number }>>([]);
  const [pathsLoading, setPathsLoading] = useState(false);

  // Close page dropdown when clicking outside
  useEffect(() => {
    if (!pageDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pageSelectorRef.current && !pageSelectorRef.current.contains(e.target as Node)) {
        setPageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pageDropdownOpen]);

  const filteredPopularPages = useMemo(() => {
    const q = pageSearch.trim().toLowerCase();
    if (!q) return popularPages;
    return popularPages.filter(p => p.url.toLowerCase().includes(q));
  }, [popularPages, pageSearch]);

  // Auto-select first page when popular pages load
  useEffect(() => {
    if (popularPages.length > 0 && selectedPage === '/') {
      setSelectedPage(popularPages[0].url);
    }
  }, [popularPages]);

  useEffect(() => {
    loadData();
  }, [timeRange, selectedPage, selectedAppId, currentPage, deviceType]);

  // Track current request to prevent stale responses from overwriting fresh ones
  const loadRequestRef = useRef(0);

  const loadData = async () => {
    // Skip loading heatmap/screenshot when selectedPage is still the default '/'
    // The useEffect will set it to the first popular page and trigger a reload
    const isDefaultPage = selectedPage === '/';

    const requestId = ++loadRequestRef.current;

    try {
      setLoading(true);

      // Convert timestamps to Date objects
      const startTime = new Date(timeRange.startTime);
      const endTime = new Date(timeRange.endTime);

      const appId = selectedAppId || 'all';

      if (isDefaultPage) {
        // Only load popular pages to discover the first real page
        const pages = await journeysService.getPopularPages(appId, startTime, endTime, 200, deviceType);
        if (requestId !== loadRequestRef.current) return; // Stale request
        setPopularPages(pages);
        setLoading(false);
        return;
      }

      // Load all data for the selected page
      const [pages, sessionsResult, heatmap, scrollData] = await Promise.all([
        journeysService.getPopularPages(appId, startTime, endTime, 200, deviceType),
        journeysService.getRecentSessions(appId, startTime, endTime, currentPage, sessionsPerPage),
        journeysService.getHeatmapData(appId, selectedPage, startTime, endTime, deviceType),
        journeysService.getScrollDepth(appId, selectedPage, startTime, endTime, deviceType),
      ]);

      if (requestId !== loadRequestRef.current) return; // Stale request

      setPopularPages(pages);
      setRecentSessions(sessionsResult.sessions);
      setSessionsPagination(sessionsResult.pagination);
      setHeatmapData(heatmap);
      setScrollDepthData(scrollData);
      setLoading(false);

      // Load screenshot asynchronously (non-blocking)
      const currentRequestId = requestId;
      journeysService.getScreenshot(appId, selectedPage, deviceType).then(screenshot => {
        // Only apply if this is still the current request
        if (currentRequestId !== loadRequestRef.current) return;
        setPageScreenshot(screenshot);
        if (screenshot) {
          console.log('[UserJourneys] Page screenshot loaded:', {
            width: screenshot.width,
            height: screenshot.height,
            size: Math.round(screenshot.screenshot.length / 1024) + 'KB',
          });
        } else {
          console.log('[UserJourneys] No screenshot available for page:', selectedPage);
        }
      }).catch(error => {
        console.error('[UserJourneys] Error loading screenshot:', error);
      });

    } catch (error) {
      console.error('Error loading journeys data:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDuration = (ms?: number): string => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const getIntensityColor = (intensity: number): string => {
    if (intensity >= 90) return 'bg-red-500/80';
    if (intensity >= 70) return 'bg-orange-500/70';
    if (intensity >= 50) return 'bg-yellow-500/60';
    if (intensity >= 30) return 'bg-green-500/50';
    return 'bg-blue-500/40';
  };

  // Load common paths for Sankey
  useEffect(() => {
    const loadPaths = async () => {
      const appId = selectedAppId || '';
      if (!appId || appId === 'all') return;
      setPathsLoading(true);
      try {
        const startTime = new Date(timeRange.startTime).toISOString();
        const endTime = new Date(timeRange.endTime).toISOString();
        const result = await productAnalyticsService.getCommonPaths(appId, 2, 5, startTime, endTime);
        setCommonPaths(result?.paths || []);
      } catch (err) {
        console.error('Failed to load common paths:', err);
      } finally {
        setPathsLoading(false);
      }
    };
    loadPaths();
  }, [selectedAppId, timeRange]);

  const selectedPageData = popularPages.find(p => p.url === selectedPage);
  const totalClicks = heatmapData?.clicks?.length || 0;
  const totalScroll = heatmapData?.scroll?.length || 0;
  const totalMovement = heatmapData?.mouseMoves?.length || 0;

  // Debug - log when data arrives
  useEffect(() => {
    if (heatmapData) {
      console.log('[UserJourneys] 🔥 HEATMAP DATA RECEIVED:', {
        clicks: totalClicks,
        scroll: totalScroll,
        movement: totalMovement,
        clicksArray: heatmapData.clicks?.length || 0,
        scrollArray: heatmapData.scroll?.length || 0,
        mouseMovesArray: heatmapData.mouseMoves?.length || 0,
        sampleClick: heatmapData.clicks?.[0],
        sampleScroll: heatmapData.scroll?.[0],
        sampleMovement: heatmapData.mouseMoves?.[0],
        fullData: heatmapData,
      });
    }
  }, [heatmapData, totalClicks, totalScroll, totalMovement]);

  // OLD: Render page snapshot using rrweb - replaced with screenshot
  // useEffect(() => {
  //   if (pageSnapshot && snapshotContainerRef.current) {
  //     ... old rrweb rebuild code removed ...
  //   }
  // }, [pageSnapshot]);
  const avgScrollDepth = scrollDepthData?.avgDepth || 0;

  const handleViewRecording = (sessionId: string) => {
    setSelectedSessionId(sessionId);
  };

  const handleCloseModal = () => {
    // Mark the session as viewed in local state when closing modal
    if (selectedSessionId) {
      setRecentSessions(prev =>
        prev.map(session =>
          session.sessionId === selectedSessionId
            ? { ...session, viewed: true }
            : session
        )
      );
    }
    setSelectedSessionId(null);
  };

  const handleToggleViewed = async (sessionId: string, currentlyViewed: boolean) => {
    try {
      await rumService.toggleSessionViewed(sessionId, !currentlyViewed);
      // Update local state without reloading entire page
      setRecentSessions(prev =>
        prev.map(session =>
          session.sessionId === sessionId
            ? { ...session, viewed: !currentlyViewed }
            : session
        )
      );
    } catch (error) {
      console.error('Failed to toggle viewed status:', error);
    }
  };

  const handleDeleteRecording = async (sessionId: string) => {
    if (!window.confirm(t('journeys.sessions.confirmDelete'))) {
      return;
    }
    try {
      await rumService.deleteRecording(sessionId);
      // Remove from local state
      setRecentSessions(prev => prev.filter(session => session.sessionId !== sessionId));
    } catch (error) {
      console.error('Failed to delete recording:', error);
    }
  };

  // Batch selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredSessions.map(s => s.sessionId);
      setSelectedSessions(new Set(allIds));
    } else {
      setSelectedSessions(new Set());
    }
  };

  const handleSelectSession = (sessionId: string, checked: boolean) => {
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(sessionId);
      } else {
        newSet.delete(sessionId);
      }
      return newSet;
    });
  };

  const handleBatchDelete = async () => {
    if (selectedSessions.size === 0) return;

    const confirmMessage = t('journeys.sessions.confirmBatchDelete', { count: selectedSessions.size });
    if (!window.confirm(confirmMessage)) return;

    setIsProcessingBatch(true);
    try {
      await rumService.batchDeleteRecordings(Array.from(selectedSessions));
      // Remove from local state
      setRecentSessions(prev => prev.filter(session => !selectedSessions.has(session.sessionId)));
      setSelectedSessions(new Set());
    } catch (error) {
      console.error('Failed to batch delete recordings:', error);
    } finally {
      setIsProcessingBatch(false);
    }
  };

  const handleBatchMarkViewed = async (viewed: boolean) => {
    if (selectedSessions.size === 0) return;

    setIsProcessingBatch(true);
    try {
      await rumService.batchToggleViewed(Array.from(selectedSessions), viewed);
      // Update local state
      setRecentSessions(prev =>
        prev.map(session =>
          selectedSessions.has(session.sessionId)
            ? { ...session, viewed }
            : session
        )
      );
      setSelectedSessions(new Set());
    } catch (error) {
      console.error('Failed to batch toggle viewed status:', error);
    } finally {
      setIsProcessingBatch(false);
    }
  };

  // Filter sessions based on viewed status
  const filteredSessions = recentSessions.filter(session => {
    if (viewedFilter === 'viewed') return session.viewed;
    if (viewedFilter === 'unviewed') return !session.viewed;
    return true; // 'all'
  });

  // Calculate scroll depth percentages
  const scrollDepthBuckets = scrollDepthData ? [
    {
      depth: '0-25%',
      users: scrollDepthData.distribution['0-25'],
      percentage: scrollDepthData.total > 0 ? (scrollDepthData.distribution['0-25'] / scrollDepthData.total) * 100 : 0,
      color: 'bg-red-500',
    },
    {
      depth: '25-50%',
      users: scrollDepthData.distribution['25-50'],
      percentage: scrollDepthData.total > 0 ? (scrollDepthData.distribution['25-50'] / scrollDepthData.total) * 100 : 0,
      color: 'bg-orange-500',
    },
    {
      depth: '50-75%',
      users: scrollDepthData.distribution['50-75'],
      percentage: scrollDepthData.total > 0 ? (scrollDepthData.distribution['50-75'] / scrollDepthData.total) * 100 : 0,
      color: 'bg-yellow-500',
    },
    {
      depth: '75-100%',
      users: scrollDepthData.distribution['75-100'],
      percentage: scrollDepthData.total > 0 ? (scrollDepthData.distribution['75-100'] / scrollDepthData.total) * 100 : 0,
      color: 'bg-green-500',
    },
  ] : [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('journeys.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('journeys.title')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('journeys.subtitle')}
          </p>
        </div>
        <Link
          to="/analytics/sdk-setup"
          className="flex items-center justify-center sm:justify-start px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors shadow-sm text-sm sm:text-base"
        >
          <CodeBracketIcon className="w-5 h-5 mr-2" />
          {t('events.setupSDK')}
        </Link>
      </div>

      {/* Time Range Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Page Selection (searchable) */}
          <div ref={pageSelectorRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('journeys.page')}
              {popularPages.length > 0 && (
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
                  ({popularPages.length})
                </span>
              )}
            </label>
            <input
              type="text"
              value={pageDropdownOpen ? pageSearch : selectedPage}
              onFocus={() => {
                setPageDropdownOpen(true);
                setPageSearch('');
              }}
              onChange={(e) => {
                setPageSearch(e.target.value);
                if (!pageDropdownOpen) setPageDropdownOpen(true);
              }}
              placeholder={popularPages.length > 0 ? '/path or search...' : tCommon('messages.noData')}
              disabled={popularPages.length === 0}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            />
            {pageDropdownOpen && popularPages.length > 0 && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg">
                {filteredPopularPages.length > 0 ? (
                  filteredPopularPages.map((page) => (
                    <button
                      key={page.url}
                      type="button"
                      onClick={() => {
                        setSelectedPage(page.url);
                        setPageDropdownOpen(false);
                        setPageSearch('');
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 dark:hover:bg-gray-600 flex items-center justify-between gap-3 ${
                        page.url === selectedPage
                          ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-900 dark:text-cyan-100'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <span className="truncate font-mono text-xs">{page.url}</span>
                      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        {formatNumber(page.views)} views
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                    {tCommon('messages.noData')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Heatmap Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('journeys.heatmapType')}
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => setHeatmapType('clicks')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  heatmapType === 'clicks'
                    ? 'bg-cyan-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <CursorArrowRaysIcon className="w-4 h-4 mx-auto mb-1" />
                {t('journeys.heatmap.clicks')}
              </button>
              <button
                onClick={() => setHeatmapType('scroll')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  heatmapType === 'scroll'
                    ? 'bg-cyan-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowsPointingOutIcon className="w-4 h-4 mx-auto mb-1" />
                {t('journeys.heatmap.scroll')}
              </button>
              <button
                onClick={() => setHeatmapType('movement')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  heatmapType === 'movement'
                    ? 'bg-cyan-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <MapIcon className="w-4 h-4 mx-auto mb-1" />
                {t('journeys.heatmap.movement')}
              </button>
            </div>
          </div>

          {/* Device Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('journeys.device')}
            </label>
            <select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 text-sm"
            >
              <option value="all">{t('journeys.allDevices')}</option>
              <option value="desktop">{t('journeys.desktop')}</option>
              <option value="mobile">{t('journeys.mobile')}</option>
              <option value="tablet">{t('journeys.tablet')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('journeys.stats.totalViews')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {formatNumber(selectedPageData?.views || 0)}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('journeys.stats.totalClicks')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {formatNumber(totalClicks)}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('journeys.stats.avgScrollDepth')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {avgScrollDepth.toFixed(0)}%
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{t('journeys.stats.uniqueVisitors')}</p>
          <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-green-600">
            {formatNumber(selectedPageData?.uniqueVisitors || 0)}
          </p>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {heatmapType === 'clicks' && t('journeys.heatmap.clickHeatmap')}
                {heatmapType === 'scroll' && t('journeys.heatmap.scrollDepthMap')}
                {heatmapType === 'movement' && t('journeys.heatmap.mouseMovementMap')}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                {heatmapType === 'clicks' && t('journeys.heatmap.clicksRecorded', { count: totalClicks })}
                {heatmapType === 'scroll' && t('journeys.heatmap.scrollEventsTracked', { count: scrollDepthData?.total || 0 })}
                {heatmapType === 'movement' && t('journeys.heatmap.mouseMovementsTracked', { count: heatmapData?.mouseMoves?.length || 0 })}
              </p>
            </div>
            <div className="hidden sm:flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">{t('journeys.heatmap.intensityLow')}</span>
                <div className="flex space-x-1">
                  <div className="w-4 h-4 bg-blue-500/40 rounded"></div>
                  <div className="w-4 h-4 bg-green-500/50 rounded"></div>
                  <div className="w-4 h-4 bg-yellow-500/60 rounded"></div>
                  <div className="w-4 h-4 bg-orange-500/70 rounded"></div>
                  <div className="w-4 h-4 bg-red-500/80 rounded"></div>
                </div>
                <span className="text-gray-600 dark:text-gray-400">{t('journeys.heatmap.intensityHigh')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Click Heatmap */}
          {heatmapType === 'clicks' && (
            <div>
              {totalClicks > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* Visual Heatmap - METADE ESQUERDA */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                      {t('journeys.heatmap.clickVisualization')}
                    </h3>
                    <div className="w-full h-[400px] sm:h-[600px] lg:h-[800px] rounded-lg overflow-auto border-2 border-gray-200 dark:border-gray-700 bg-white">
                      {pageScreenshot ? (
                        <div className="relative w-full">
                          <img
                            src={pageScreenshot.screenshot}
                            alt="Page screenshot"
                            className="block w-full h-auto"
                          />
                          <HeatmapCanvas
                            clicks={heatmapData.clicks}
                            width={pageScreenshot.width}
                            height={pageScreenshot.height}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-800">
                          <p className="text-gray-500">{t('journeys.heatmap.loadingVisualization')}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                      {t('journeys.heatmap.clicksTrackedOnPage', { count: totalClicks })}
                    </p>
                  </div>

                  {/* Click breakdown - METADE DIREITA */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                      {t('journeys.heatmap.topClickedElements')}
                    </h3>
                    <div className="space-y-3 h-[400px] sm:h-[600px] lg:h-[800px] overflow-y-auto pr-2">
                      {/* Group clicks by element and show top elements */}
                      {Object.entries(
                        heatmapData.clicks.reduce((acc: any, click: any) => {
                          const elem = click.element || t('journeys.heatmap.unknownElement');
                          acc[elem] = (acc[elem] || 0) + 1;
                          return acc;
                        }, {})
                      )
                        .sort((a, b) => (b[1] as number) - (a[1] as number))
                        .slice(0, 50)
                        .map(([element, count], index) => {
                          const percentage = ((count as number) / totalClicks) * 100;
                          return (
                            <div key={element} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-cyan-500 dark:hover:border-cyan-500 transition-all hover:shadow-md">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
                                    index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg' :
                                    index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 shadow-md' :
                                    index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 shadow-md' :
                                    'bg-gradient-to-br from-cyan-500 to-blue-600'
                                  }`}>
                                    {index + 1}
                                  </div>
                                  <span className="text-sm text-gray-700 dark:text-gray-300 font-mono truncate">
                                    {element}
                                  </span>
                                </div>
                                <div className="text-right flex-shrink-0 ml-3">
                                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {count}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {percentage.toFixed(1)}%
                                  </div>
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 transition-all duration-500 rounded-full relative overflow-hidden"
                                  style={{ width: `${percentage}%` }}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <CursorArrowRaysIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold mb-2">{t('journeys.heatmap.noClickData')}</p>
                  <p className="text-sm">
                    {t('journeys.heatmap.noClickDataDesc')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Scroll Depth Map */}
          {heatmapType === 'scroll' && scrollDepthBuckets.length > 0 && (
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
                {/* Scroll visualization */}
                <div className="flex-1">
                  <div className="relative h-64 sm:h-96 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
                    {scrollDepthBuckets.map((depth, index) => (
                      <div
                        key={index}
                        className="relative"
                        style={{ height: '25%' }}
                      >
                        <div
                          className={`absolute inset-0 ${depth.color} transition-all duration-500`}
                          style={{ opacity: depth.percentage / 100 }}
                        ></div>
                        <div className="relative z-10 flex items-center justify-center h-full">
                          <div className="text-center">
                            <p className="text-white font-bold text-sm sm:text-lg">{depth.percentage.toFixed(1)}%</p>
                            <p className="text-white text-xs sm:text-sm">{formatNumber(depth.users)} users</p>
                          </div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-px bg-white/30"></div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{t('journeys.heatmap.pageTop')}</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>{t('journeys.heatmap.pageBottom')}</span>
                  </div>
                </div>

                {/* Scroll stats */}
                <div className="w-full lg:w-80 grid grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
                  {scrollDepthBuckets.map((depth, index) => (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {depth.depth}
                        </span>
                        <span className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white">
                          {depth.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${depth.color} transition-all duration-500`}
                          style={{ width: `${depth.percentage}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                        {t('journeys.heatmap.usersReachedDepth', { count: formatNumber(depth.users) })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mouse Movement Map */}
          {heatmapType === 'movement' && (
            <div>
              {heatmapData?.mouseMoves?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* Visual Movement Heatmap - METADE ESQUERDA */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                      {t('journeys.heatmap.mouseMovementDensity')}
                    </h3>
                    <div className="w-full h-[400px] sm:h-[600px] lg:h-[800px] rounded-lg overflow-auto border-2 border-gray-200 dark:border-gray-700 bg-white">
                      {pageScreenshot ? (
                        <div className="relative w-full">
                          <img
                            src={pageScreenshot.screenshot}
                            alt="Page screenshot"
                            className="block w-full h-auto"
                          />
                          <MouseMovementCanvas
                            movements={heatmapData.mouseMoves}
                            width={pageScreenshot.width}
                            height={pageScreenshot.height}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-800">
                          <p className="text-gray-500">{t('journeys.heatmap.loadingVisualization')}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                      {t('journeys.heatmap.mouseMovementsTrackedOnPage', { count: heatmapData.mouseMoves.length })}
                    </p>
                  </div>

                  {/* Movement Statistics - METADE DIREITA */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                      {t('journeys.heatmap.movementStatistics')}
                    </h3>
                    <div className="space-y-4 h-[400px] sm:h-[600px] lg:h-[800px] overflow-y-auto pr-2">
                      {/* Total Movements */}
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{t('journeys.heatmap.totalMovements')}</p>
                            <p className="text-4xl font-bold text-blue-600 mt-2">{formatNumber(heatmapData.mouseMoves.length)}</p>
                          </div>
                          <MapIcon className="w-16 h-16 text-blue-500 opacity-20" />
                        </div>
                      </div>

                      {/* Heat Zones */}
                      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                          {t('journeys.heatmap.mostActiveAreas')}
                        </h4>
                        <div className="space-y-3">
                          {/* Calculate heat zones by dividing page into grid */}
                          {(() => {
                            const gridSize = 100; // 100px grid
                            const zones: Record<string, number> = {};

                            heatmapData.mouseMoves.forEach((move: any) => {
                              const gridX = Math.floor(move.x / gridSize);
                              const gridY = Math.floor(move.y / gridSize);
                              const key = `${gridX},${gridY}`;
                              zones[key] = (zones[key] || 0) + 1;
                            });

                            return Object.entries(zones)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 10)
                              .map(([coords, count], index) => {
                                const [x, y] = coords.split(',').map(Number);
                                const percentage = ((count as number) / heatmapData.mouseMoves.length) * 100;

                                return (
                                  <div key={coords} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center space-x-2">
                                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white ${
                                          index === 0 ? 'bg-gradient-to-br from-blue-500 to-purple-600' :
                                          index === 1 ? 'bg-gradient-to-br from-purple-500 to-pink-600' :
                                          'bg-blue-500'
                                        }`}>
                                          {index + 1}
                                        </div>
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {t('journeys.heatmap.zone')} ({x * gridSize}px, {y * gridSize}px)
                                        </span>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-gray-900 dark:text-white">{count}</div>
                                        <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                                      </div>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                      <div
                                        className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full"
                                        style={{ width: `${percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                );
                              });
                          })()}
                        </div>
                      </div>

                      {/* Movement Patterns */}
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 p-6 rounded-lg border-2 border-blue-200 dark:border-blue-900">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          💡 {t('journeys.heatmap.insights')}
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                          <li className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            <span>{t('journeys.heatmap.insightAttention')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-purple-500 mr-2">•</span>
                            <span>{t('journeys.heatmap.insightNavigation')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-pink-500 mr-2">•</span>
                            <span>{t('journeys.heatmap.insightOptimize')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <MapIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold mb-2">{t('journeys.heatmap.noMovementData')}</p>
                  <p className="text-sm">
                    {t('journeys.heatmap.noMovementDataDesc')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Path Analysis / Sankey Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('journeys.pathAnalysis')}</h2>
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setPathViewMode('list')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                pathViewMode === 'list' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ListBulletIcon className="w-4 h-4" />
              <span>List</span>
            </button>
            <button
              onClick={() => setPathViewMode('sankey')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                pathViewMode === 'sankey' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ChartBarSquareIcon className="w-4 h-4" />
              <span>{t('journeys.visualization.sankey')}</span>
            </button>
          </div>
        </div>

        {pathsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          </div>
        ) : commonPaths.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p>{t('journeys.noData')}</p>
          </div>
        ) : pathViewMode === 'sankey' ? (
          <div className="overflow-x-auto">
            <SankeyDiagram paths={commonPaths} />
          </div>
        ) : (
          <div className="space-y-3">
            {commonPaths.slice(0, 15).map((path, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <span className="text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 shrink-0">{i + 1}</span>
                  <div className="flex items-center space-x-1 min-w-0 overflow-x-auto">
                    {path.sequence.map((step, si) => (
                      <span key={si} className="flex items-center shrink-0">
                        {si > 0 && <span className="text-gray-400 mx-1">→</span>}
                        <span className="text-sm px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 whitespace-nowrap">{step}</span>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-3 shrink-0 ml-3">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{path.count} {t('journeys.commonPaths.users').toLowerCase()}</span>
                  <span className="text-xs text-gray-500">{path.conversionRate.toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Sessions Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {t('journeys.sessions.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('journeys.sessions.sessionsFound', { count: sessionsPagination?.total || 0 })}
            </p>
          </div>

          {/* Viewed Filter */}
          <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto">
            <button
              onClick={() => setViewedFilter('all')}
              className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                viewedFilter === 'all'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('journeys.sessions.all')}
            </button>
            <button
              onClick={() => setViewedFilter('unviewed')}
              className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                viewedFilter === 'unviewed'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('journeys.sessions.unviewed')}
            </button>
            <button
              onClick={() => setViewedFilter('viewed')}
              className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                viewedFilter === 'viewed'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('journeys.sessions.viewed')}
            </button>
          </div>
        </div>

        {/* Batch Actions Bar */}
        {selectedSessions.size > 0 && (
          <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg">
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {t('journeys.sessions.selectedCount', { count: selectedSessions.size })}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBatchMarkViewed(true)}
                disabled={isProcessingBatch}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <EyeIcon className="w-4 h-4 mr-1" />
                {t('journeys.sessions.markAsViewed')}
              </button>
              <button
                onClick={() => handleBatchMarkViewed(false)}
                disabled={isProcessingBatch}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <EyeSlashIcon className="w-4 h-4 mr-1" />
                {t('journeys.sessions.markAsUnviewed')}
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={isProcessingBatch}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <TrashIcon className="w-4 h-4 mr-1" />
                {t('journeys.sessions.deleteSelected')}
              </button>
              <button
                onClick={() => setSelectedSessions(new Set())}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                {t('journeys.sessions.clearSelection')}
              </button>
            </div>
          </div>
        )}

        {filteredSessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-center w-12">
                    <input
                      type="checkbox"
                      checked={filteredSessions.length > 0 && selectedSessions.size === filteredSessions.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.sessionId')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.startedAt')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.duration')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.device')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.location')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.status')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('journeys.sessions.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSessions.map((session) => (
                  <tr key={session.sessionId} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedSessions.has(session.sessionId) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}>
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedSessions.has(session.sessionId)}
                        onChange={(e) => handleSelectSession(session.sessionId, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">
                        {session.sessionId.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {new Date(session.startedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDuration(session.duration)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {session.device.type} / {session.device.browser}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {session.location.city || session.location.country || t('journeys.sessions.unknownLocation')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleViewed(session.sessionId, session.viewed || false)}
                        className={`inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                          session.viewed
                            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50'
                        }`}
                        title={session.viewed ? t('journeys.sessions.markAsUnviewed') : t('journeys.sessions.markAsViewed')}
                      >
                        {session.viewed ? (
                          <>
                            <EyeIcon className="w-4 h-4" />
                            <span>{t('journeys.sessions.viewed')}</span>
                          </>
                        ) : (
                          <>
                            <EyeSlashIcon className="w-4 h-4" />
                            <span>{t('journeys.sessions.unviewed')}</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewRecording(session.sessionId)}
                          className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                          title={t('journeys.sessions.viewRecording')}
                        >
                          <VideoCameraIcon className="w-4 h-4" />
                          <span className="hidden sm:inline">{t('journeys.sessions.viewRecording')}</span>
                        </button>
                        <button
                          onClick={() => handleDeleteRecording(session.sessionId)}
                          className="inline-flex items-center space-x-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title={t('journeys.sessions.deleteRecording')}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {(sessionsPagination?.totalPages || 0) > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
                  {t('journeys.sessions.showing', {
                    from: ((sessionsPagination?.page || 1) - 1) * (sessionsPagination?.limit || 10) + 1,
                    to: Math.min((sessionsPagination?.page || 1) * (sessionsPagination?.limit || 10), sessionsPagination?.total || 0),
                    total: sessionsPagination?.total || 0,
                  })}
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="hidden sm:inline">{t('journeys.sessions.previous')}</span>
                    <span className="sm:hidden">←</span>
                  </button>
                  <div className="flex items-center space-x-1">
                    {/* Show limited page numbers on mobile */}
                    {Array.from({ length: sessionsPagination?.totalPages || 0 }, (_, i) => i + 1)
                      .filter(page => {
                        // On mobile, show first, last, current and adjacent pages
                        const totalPages = sessionsPagination?.totalPages || 0;
                        return page === 1 ||
                               page === totalPages ||
                               Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, arr) => (
                        <span key={page} className="flex items-center">
                          {/* Add ellipsis if there's a gap */}
                          {index > 0 && arr[index - 1] !== page - 1 && (
                            <span className="px-1 text-gray-400">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-cyan-500 text-white'
                                : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        </span>
                      ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(sessionsPagination?.totalPages || 1, p + 1))}
                    disabled={currentPage === (sessionsPagination?.totalPages || 1)}
                    className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="hidden sm:inline">{t('journeys.sessions.next')}</span>
                    <span className="sm:hidden">→</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <PlayIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>{t('journeys.sessions.noSessions')}</p>
            <p className="text-sm mt-2">{t('journeys.sessions.noSessionsDescription')}</p>
          </div>
        )}

        <div className="mt-4 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
          <p className="text-sm text-cyan-800 dark:text-cyan-400">
            <strong>💡 {t('journeys.sessions.tip')}:</strong> {t('journeys.sessions.installTip')}
          </p>
        </div>
      </div>

      </div>

      {/* Session Replay Modal */}
      {selectedSessionId && (
        <SessionReplayModal
          sessionId={selectedSessionId}
          onClose={handleCloseModal}
        />
      )}
    </MainLayout>
  );
}
